import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Share2, Check, X, Plus, Copy, ExternalLink } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import YahrzeitTable from '@/components/yahrzeit/YahrzeitTable';
import YahrzeitForm from '@/components/yahrzeit/YahrzeitForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const YahrzeitManager: React.FC = () => {
  const { direction } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedYahrzeit, setSelectedYahrzeit] = useState<any>(null);
  const [yahrzeits, setYahrzeits] = useState<any[]>([]);
  const [pendingYahrzeits, setPendingYahrzeits] = useState<any[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [publicLink, setPublicLink] = useState('');

  // Function to fetch yahrzeits
  const fetchYahrzeits = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Fetch approved yahrzeits
      const { data: approvedData, error: approvedError } = await supabase
        .from('yahrzeits')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'approved')
        .order('gregorian_date', { ascending: false });
      
      if (approvedError) throw approvedError;
      setYahrzeits(approvedData || []);
      
      // Fetch pending yahrzeits
      const { data: pendingData, error: pendingError } = await supabase
        .from('yahrzeits')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (pendingError) throw pendingError;
      setPendingYahrzeits(pendingData || []);
    } catch (error) {
      console.error('Error fetching yahrzeits:', error);
      toast({
        variant: 'destructive',
        title: 'Error fetching yahrzeits',
        description: 'Failed to load yahrzeit data. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchYahrzeits();
    }
  }, [user]);

  // Generate public link when share dialog opens
  useEffect(() => {
    if (shareDialogOpen && user) {
      const baseUrl = window.location.origin;
      setPublicLink(`${baseUrl}/yahrzeit/public/${user.id}`);
    }
  }, [shareDialogOpen, user]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(publicLink);
    toast({
      title: 'Link Copied',
      description: 'Public form link has been copied to clipboard.',
    });
  };

  const handleApproveYahrzeit = async (id: string) => {
    try {
      const { error } = await supabase
        .from('yahrzeits')
        .update({ status: 'approved' })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      toast({
        title: 'Yahrzeit Approved',
        description: 'The yahrzeit has been approved and will appear in displays.',
      });
      
      fetchYahrzeits();
    } catch (error) {
      console.error('Error approving yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to approve the yahrzeit. Please try again.',
      });
    }
  };

  const handleRejectYahrzeit = async (id: string) => {
    try {
      const { error } = await supabase
        .from('yahrzeits')
        .update({ status: 'rejected' })
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      toast({
        title: 'Yahrzeit Rejected',
        description: 'The yahrzeit has been rejected.',
      });
      
      fetchYahrzeits();
    } catch (error) {
      console.error('Error rejecting yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reject the yahrzeit. Please try again.',
      });
    }
  };

  const handleAddYahrzeit = async (values: any) => {
    try {
      const { error } = await supabase
        .from('yahrzeits')
        .insert([{
          ...values,
          user_id: user?.id,
          status: 'approved'
        }]);
      
      if (error) throw error;
      
      toast({
        title: 'Yahrzeit Added',
        description: 'The yahrzeit has been added successfully.',
      });
      
      setShowAddForm(false);
      fetchYahrzeits();
    } catch (error) {
      console.error('Error adding yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to add the yahrzeit. Please try again.',
      });
    }
  };

  const handleDeleteYahrzeit = async (id: string) => {
    try {
      const { error } = await supabase
        .from('yahrzeits')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      toast({
        title: 'Yahrzeit Deleted',
        description: 'The yahrzeit has been deleted successfully.',
      });
      
      fetchYahrzeits();
    } catch (error) {
      console.error('Error deleting yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete the yahrzeit. Please try again.',
      });
    }
  };

  const handleEditYahrzeit = async (values: any) => {
    try {
      const { error } = await supabase
        .from('yahrzeits')
        .update({
          name: values.name,
          father_name: values.father_name,
          gregorian_date: values.gregorian_date,
          hebrew_date: values.hebrew_date,
          notes: values.notes
        })
        .eq('id', selectedYahrzeit.id)
        .eq('user_id', user?.id);
      
      if (error) throw error;
      
      toast({
        title: 'Yahrzeit Updated',
        description: 'The yahrzeit has been updated successfully.',
      });
      
      setShowEditForm(false);
      setSelectedYahrzeit(null);
      fetchYahrzeits();
    } catch (error) {
      console.error('Error updating yahrzeit:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update the yahrzeit. Please try again.',
      });
    }
  };

  const handleEditClick = (yahrzeit: any) => {
    setSelectedYahrzeit(yahrzeit);
    setShowEditForm(true);
  };

  return (
    <div className={direction === 'rtl' ? 'rtl' : 'ltr'}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">ניהול יארצייט</h1>
        <p className="text-muted-foreground">ניהול ותצוגה של רשומות יארצייט</p>
      </div>
      
      <div className="flex justify-between mb-6">
        <Button 
          variant="outline" 
          onClick={() => setShowAddForm(true)}
        >
          <Plus className="ml-2 h-4 w-4" />
          הוסף יארצייט
        </Button>
        
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-synagogue-blue hover:bg-synagogue-accent">
              <Share2 className="ml-2 h-4 w-4" />
              שתף טופס ציבורי
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>שיתוף טופס ציבורי</DialogTitle>
              <DialogDescription>
                כל מי שיש לו קישור זה יכול להגיש מידע על יארצייט עבור החשבון שלך.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-2">
                <div className="grid flex-1 gap-2">
                  <div className="flex items-center border rounded-md px-3 py-2">
                    <span className="text-sm font-medium break-all">{publicLink}</span>
                  </div>
                </div>
                <Button type="button" size="sm" className="px-3 shrink-0" onClick={handleCopyLink}>
                  <span className="sr-only">Copy</span>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline" 
                  className="flex items-center" 
                  onClick={() => window.open(publicLink, '_blank')}
                >
                  <span>פתח בחלון חדש</span>
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      <Tabs defaultValue="approved" className="space-y-4">
        <TabsList>
          <TabsTrigger value="approved">מאושרים</TabsTrigger>
          <TabsTrigger value="pending">ממתינים</TabsTrigger>
        </TabsList>
        
        <TabsContent value="approved">
          <Card>
            <CardHeader>
              <CardTitle>יארצייט מאושרים</CardTitle>
              <CardDescription>
                רשימת היארצייט המאושרים שלך
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4 md:p-6 max-w-4xl mx-auto">
              <div className="overflow-x-auto">
                <YahrzeitTable 
                  yahrzeits={yahrzeits} 
                  onDelete={handleDeleteYahrzeit}
                  onEdit={handleEditClick}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>בקשות יארצייט ממתינות</CardTitle>
              <CardDescription>בקשות שהוגשו דרך הטופס הציבורי ומחכות לאישור</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-center py-4">טוען...</p>
              ) : pendingYahrzeits.length === 0 ? (
                <p className="text-center py-4">אין בקשות ממתינות לאישור</p>
              ) : (
                <div className="space-y-4">
                  {pendingYahrzeits.map((yahrzeit) => (
                    <Card key={yahrzeit.id} className="p-4 bg-muted/50 border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h3 className="font-bold">{yahrzeit.name}</h3>
                          {yahrzeit.father_name && <p>בן/בת {yahrzeit.father_name}</p>}
                          <p className="text-sm">תאריך: {new Date(yahrzeit.gregorian_date).toLocaleDateString()}</p>
                          {yahrzeit.hebrew_date && <p className="text-sm">תאריך עברי: {yahrzeit.hebrew_date}</p>}
                          {yahrzeit.notes && <p className="text-sm mt-2">{yahrzeit.notes}</p>}
                        </div>
                        <div className="flex flex-col justify-between">
                          {yahrzeit.submitted_by && (
                            <p className="text-sm text-muted-foreground">
                              הוגש על ידי: {yahrzeit.submitted_by}
                            </p>
                          )}
                          <div className="flex justify-end gap-2 mt-4">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="bg-red-50 hover:bg-red-100 border-red-200"
                              onClick={() => handleRejectYahrzeit(yahrzeit.id)}
                            >
                              <X className="h-4 w-4 ml-1" />
                              דחה
                            </Button>
                            <Button 
                              variant="outline"
                              size="sm"
                              className="bg-green-50 hover:bg-green-100 border-green-200"
                              onClick={() => handleApproveYahrzeit(yahrzeit.id)}
                            >
                              <Check className="h-4 w-4 ml-1" />
                              אשר
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Form Dialog */}
      <Dialog open={showAddForm} onOpenChange={setShowAddForm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>הוסף יארצייט חדש</DialogTitle>
            <DialogDescription>
              הזן את פרטי היארצייט החדש
            </DialogDescription>
          </DialogHeader>
          <YahrzeitForm 
            onSubmit={handleAddYahrzeit} 
            onCancel={() => setShowAddForm(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Form Dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>ערוך יארצייט</DialogTitle>
            <DialogDescription>
              עדכן את פרטי היארצייט
            </DialogDescription>
          </DialogHeader>
          {selectedYahrzeit && (
            <YahrzeitForm 
              onSubmit={handleEditYahrzeit} 
              initialValues={selectedYahrzeit}
              onCancel={() => {
                setShowEditForm(false);
                setSelectedYahrzeit(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YahrzeitManager;
